import React, { useEffect, useMemo, useState } from "react";
import "./ThesaurusGraph.css";

const COLOUR = {
  root: "#c7902e",
  books: "#b87922",
  peoples: "#78a566",
  symbols: "#5f83ad",
  places: "#66a7a1",
  characters: "#7e5a91",
  moments: "#a85d58",
  pure: "#d7b56d",
};

const NODES = [
  {
    id: "sons",
    title: "Sons of the Standard",
    label: "Sons of\nthe Standard",
    kind: "root",
    x: 470,
    y: 380,
    r: 66,
    icon: "✦",
    detail: {
      type: "Storyworld Root",
      tags: ["Books", "Symbols", "Peoples", "Places"],
      story:
        "The central doorway into the Roman Storyworld: books, peoples, places, symbols, character voices and story moments gathered around one living map.",
      meaning:
        "A visual centre for the language, memory and mythology of the Sons of the Standard series.",
      symbolism:
        "Standard, oath, belonging, rupture, return and the struggle to decide what survives after empire.",
      appears: ["Blood and Honour", "Shadows of the Eagle"],
      related: ["Eagle", "Boar", "Varro", "Aerwyna"],
    },
  },

  {
    id: "books",
    title: "Books",
    label: "Books",
    kind: "books",
    x: 280,
    y: 280,
    r: 48,
    icon: "▤",
    detail: {
      type: "Storyworld Area",
      tags: ["Books"],
      story: "The books of the Sons of the Standard sequence.",
      meaning: "A doorway into the novels without disturbing the practical thesaurus.",
      symbolism: "Chronicle, witness and memory.",
      appears: ["Blood and Honour", "Shadows of the Eagle"],
      related: ["Blood and Honour", "Shadows of the Eagle"],
    },
  },
  {
    id: "peoples",
    title: "Peoples",
    label: "Peoples",
    kind: "peoples",
    x: 490,
    y: 170,
    r: 52,
    icon: "♟",
    detail: {
      type: "Storyworld Area",
      tags: ["Peoples"],
      story:
        "Romans, Cornovii, Ordovices, Deceangli, Brigantes and Scoti: the human forces that shape the world.",
      meaning:
        "Each people carries alliances, fractures, fears, ambitions and memories.",
      symbolism: "Belonging, blood, oath, land and contested identity.",
      appears: ["Blood and Honour", "Shadows of the Eagle"],
      related: ["Romans", "Cornovii", "Ordovices", "Deceangli"],
    },
  },
  {
    id: "symbols",
    title: "Symbols & Meaning",
    label: "Symbols &\nMeaning",
    kind: "symbols",
    x: 705,
    y: 280,
    r: 54,
    icon: "◎",
    detail: {
      type: "Storyworld Area",
      tags: ["Symbols"],
      story:
        "Objects and natural forces that carry emotional and spiritual weight across the books.",
      meaning: "Where objects become memory, ritual and belief.",
      symbolism:
        "Empire, freedom, fire, water, stone, sky, oath and release.",
      appears: ["Blood and Honour", "Shadows of the Eagle"],
      related: ["Eagle", "Boar", "Torc", "Fire", "Water"],
    },
  },
  {
    id: "places",
    title: "Places",
    label: "Places",
    kind: "places",
    x: 260,
    y: 540,
    r: 48,
    icon: "⌂",
    detail: {
      type: "Storyworld Area",
      tags: ["Places"],
      story:
        "The landscapes, forts, roads, coastlines and settlements through which the story moves.",
      meaning:
        "Place is not background. The land shapes choices, memory and survival.",
      symbolism: "Road, ridge, river, island, fortress, refuge and return.",
      appears: ["Blood and Honour", "Shadows of the Eagle"],
      related: ["Deva", "Mona", "Viriconium", "Din Lligwy"],
    },
  },
  {
    id: "characters",
    title: "Character Voices",
    label: "Character\nVoices",
    kind: "characters",
    x: 650,
    y: 565,
    r: 48,
    icon: "◔",
    detail: {
      type: "Storyworld Area",
      tags: ["Character Voices"],
      story: "The same word changes meaning depending on who speaks it.",
      meaning:
        "This area helps preserve character voice and cultural viewpoint.",
      symbolism: "Voice, witness, loyalty, grief and chosen allegiance.",
      appears: ["Blood and Honour", "Shadows of the Eagle"],
      related: ["Varro", "Aerwyna", "Lucius", "Nestor"],
    },
  },
  {
    id: "moments",
    title: "Story Moments",
    label: "Story\nMoments",
    kind: "moments",
    x: 470,
    y: 665,
    r: 48,
    icon: "✦",
    detail: {
      type: "Storyworld Area",
      tags: ["Story Moments"],
      story: "Key episodes that connect to your timeline-style pages.",
      meaning:
        "A bridge between the mythology map and reader-facing story moments.",
      symbolism: "Turning points, sacrifice, rupture, oath and consequence.",
      appears: ["Blood and Honour", "Shadows of the Eagle"],
      related: ["Arena", "The Forge", "Raid on Mona", "Ashes and Oaths"],
    },
  },
  {
    id: "pure",
    title: "Ancient Roman Terms Thesaurus",
    label: "Ancient Roman\nTerms Thesaurus",
    kind: "pure",
    x: 190,
    y: 450,
    r: 60,
    icon: "SPQR",
    action: "pure",
    detail: {
      type: "Pure Thesaurus",
            story:
        "The original thesaurus remains here as a clean practical tool for ancient Roman vocabulary, categories and related terms.",
      meaning:
        "This keeps the writer-facing reference tool separate from the storyworld mythology map.",
      symbolism:
        "Scholarship, clarity, craft and the disciplined naming of the ancient world.",
      appears: ["Research", "Writing Tool"],
      related: ["Military Terms", "Architecture", "Government", "Daily Life"],
    },
  },

  {
    id: "blood",
    title: "Blood and Honour",
    label: "Blood and\nHonour",
    kind: "books",
    x: 185,
    y: 220,
    r: 30,
    detail: {
      type: "Book",
      story:
        "Varro, Legio XX, Deva, Paulus Catena, the arena and the struggle to reclaim honour from empire.",
      meaning: "Fallen allegiance, survival and chosen brotherhood.",
      symbolism: "Eagle, arena, boar, blood and oath.",
      appears: ["Book One"],
      related: ["Varro", "Eagle", "Boar", "Arena"],
    },
  },
  {
    id: "shadows",
    title: "Shadows of the Eagle",
    label: "Shadows of\nthe Eagle",
    kind: "books",
    x: 175,
    y: 330,
    r: 30,
    detail: {
      type: "Book",
      story:
        "Aerwyna, Lucius, Varro, Mona, the tribes and the cost of resistance after the eagle falls from Roman certainty.",
      meaning: "Tribal alliance, spiritual authority, fracture and survival.",
      symbolism: "Lake, eagle, torc, fire, land and lights.",
      appears: ["Book Two"],
      related: ["Aerwyna", "Lucius", "Mona", "Ashes and Oaths"],
    },
  },

  {
    id: "romans",
    title: "Romans",
    label: "Romans",
    kind: "peoples",
    x: 345,
    y: 115,
    r: 29,
    detail: {
      type: "People",
      story:
        "Rome appears through soldiers, administrators, physicians, road-builders and men caught inside a machine larger than themselves.",
      meaning: "Order can protect, consume, discipline and erase.",
      symbolism: "Roads, standards, law, iron and command.",
      appears: ["Blood and Honour", "Shadows of the Eagle"],
      related: ["Eagle", "Varro", "Deva"],
    },
  },
  {
    id: "cornovii",
    title: "Cornovii",
    label: "Cornovii",
    kind: "peoples",
    x: 430,
    y: 85,
    r: 29,
    detail: {
      type: "People",
      story:
        "The Cornovii stand between accommodation, pride, survival and the desire to recover something older than Rome.",
      meaning: "Political caution set against cultural memory.",
      symbolism: "Hall, oath, boar, land and divided counsel.",
      appears: ["Blood and Honour", "Shadows of the Eagle"],
      related: ["Lucius", "Viriconium", "Boar"],
    },
  },
  {
    id: "ordovices",
    title: "Ordovices",
    label: "Ordovices",
    kind: "peoples",
    x: 520,
    y: 82,
    r: 31,
    detail: {
      type: "People",
      story:
        "The Ordovices are bound to highland, memory and resistance, given renewed focus through Aerwyna’s authority.",
      meaning: "Belonging rooted in land rather than imperial command.",
      symbolism: "Mountain, oath, fire, sky and ancestral witness.",
      appears: ["Shadows of the Eagle"],
      related: ["Aerwyna", "Torc", "The Forge"],
    },
  },
  {
    id: "deceangli",
    title: "Deceangli",
    label: "Deceangli",
    kind: "peoples",
    x: 610,
    y: 95,
    r: 29,
    detail: {
      type: "People",
      story:
        "The Deceangli bring riders, scouts and the hard politics of alliance.",
      meaning: "Resistance as practical action, not just speech.",
      symbolism: "Horse, coast, warning and hard terrain.",
      appears: ["Shadows of the Eagle"],
      related: ["The Double Enemy", "Ridgeway"],
    },
  },
  {
    id: "brigantes",
    title: "Brigantes",
    label: "Brigantes",
    kind: "peoples",
    x: 640,
    y: 160,
    r: 29,
    detail: {
      type: "People",
      story:
        "The Brigantes stand as a powerful northern force, enemy and reminder that Britain is not one united people.",
      meaning: "Freedom complicated by rival tribes, ambition and old divisions.",
      symbolism: "Northern pressure, raid, fracture and survival.",
      appears: ["Blood and Honour", "Shadows of the Eagle"],
      related: ["The Double Enemy", "Scoti"],
    },
  },
  {
    id: "scoti",
    title: "Scoti",
    label: "Scoti",
    kind: "peoples",
    x: 620,
    y: 240,
    r: 28,
    detail: {
      type: "People",
      story:
        "The Scoti are a sea-borne danger, raiders and destabilising pressure.",
      meaning: "Opportunism at the edge of order.",
      symbolism: "Sea, raid, fear, hunger and the broken coast.",
      appears: ["Shadows of the Eagle"],
      related: ["Mona", "The Double Enemy"],
    },
  },

  {
    id: "eagle",
    title: "Eagle",
    label: "Eagle",
    kind: "symbols",
    x: 705,
    y: 115,
    r: 32,
    detail: {
      type: "Symbol",
      tags: ["Symbol", "Roman", "Empire"],
      story:
        "The eagle begins as the imperial standard of Rome, a symbol of power and command. Over time, it becomes a burden of memory for Varro and the men who once served beneath it.",
      meaning:
        "Duty, lost honour, imperial identity, the weight of allegiance and the painful severing of loyalty.",
      symbolism:
        "Empire, command, sacrifice, memory, authority, vision and release.",
      appears: ["Blood and Honour", "Shadows of the Eagle"],
      related: [
        "Roman Legions & Bases",
        "Military Terms",
        "Standards",
        "Honour",
        "Varro",
      ],
    },
  },
  {
    id: "boar",
    title: "Boar",
    label: "Boar",
    kind: "symbols",
    x: 825,
    y: 175,
    r: 29,
    detail: {
      type: "Symbol",
      story: "The boar becomes a hardier emblem for the cohort’s changed identity.",
      meaning: "Defiance, ferocity, endurance and chosen brotherhood.",
      symbolism: "Tusks, earth, woodland, resistance and wounded strength.",
      appears: ["Blood and Honour", "Shadows of the Eagle"],
      related: ["Cornovii", "Varro", "Lucius"],
    },
  },
  {
    id: "torc",
    title: "Torc",
    label: "Torc",
    kind: "symbols",
    x: 885,
    y: 260,
    r: 28,
    detail: {
      type: "Symbol",
      story: "The torc marks rank, belonging, obligation and memory.",
      meaning: "Authority worn as burden rather than ornament.",
      symbolism: "Neck-ring, oath, status, ancestry and binding.",
      appears: ["Shadows of the Eagle"],
      related: ["Aerwyna", "Ordovices"],
    },
  },
  {
    id: "spear",
    title: "Spear",
    label: "Spear",
    kind: "symbols",
    x: 890,
    y: 355,
    r: 28,
    detail: {
      type: "Symbol",
      story: "The spear is weapon, witness and oath-marker when grounded in the earth.",
      meaning: "A decision made in public.",
      symbolism: "War, pledge, land, line and judgement.",
      appears: ["Blood and Honour", "Shadows of the Eagle"],
      related: ["The Forge"],
    },
  },
  {
    id: "fire",
    title: "Fire",
    label: "Fire",
    kind: "symbols",
    x: 845,
    y: 445,
    r: 28,
    detail: {
      type: "Symbol",
      story: "Fire warms, destroys, purifies and reveals.",
      meaning: "Transformation at a cost.",
      symbolism: "Forge, pyre, hearth, warning and renewal.",
      appears: ["Shadows of the Eagle"],
      related: ["The Forge", "Ashes and Oaths"],
    },
  },
  {
    id: "water",
    title: "Water",
    label: "Water",
    kind: "symbols",
    x: 760,
    y: 490,
    r: 28,
    detail: {
      type: "Symbol",
      story: "Water carries crossing, cleansing, burial and release.",
      meaning: "Threshold between old allegiance and new freedom.",
      symbolism: "Lake, river, sea, passage and surrender.",
      appears: ["Shadows of the Eagle"],
      related: ["Mona", "Ashes and Oaths"],
    },
  },
  {
    id: "stone",
    title: "Stone",
    label: "Stone",
    kind: "symbols",
    x: 620,
    y: 470,
    r: 28,
    detail: {
      type: "Symbol",
      story: "Stone is memory made physical.",
      meaning: "The land keeps what people forget.",
      symbolism: "Hillfort, cairn, road, ruin and witness.",
      appears: ["Blood and Honour", "Shadows of the Eagle"],
      related: ["Ridgeway", "The Lights that Remain"],
    },
  },

  {
    id: "deva",
    title: "Deva",
    label: "Deva",
    kind: "places",
    x: 120,
    y: 525,
    r: 27,
    detail: {
      type: "Place",
      story: "The fortress city of Legio XX and the centre of Varro’s fall and return.",
      meaning: "Order, captivity and contested memory.",
      symbolism: "Fortress, gate, amphitheatre and river.",
      appears: ["Blood and Honour"],
      related: ["Varro", "Arena", "Romans"],
    },
  },
  {
    id: "mona",
    title: "Mona",
    label: "Mona",
    kind: "places",
    x: 105,
    y: 605,
    r: 27,
    detail: {
      type: "Place",
      story: "Mona is mine, island, sacred memory and battleground.",
      meaning: "A place where empire extracts and older powers endure.",
      symbolism: "Island, mine, sea, captive labour and ritual.",
      appears: ["Shadows of the Eagle"],
      related: ["Raid on Mona", "Nestor", "Water"],
    },
  },
  {
    id: "viriconium",
    title: "Viriconium",
    label: "Viriconium",
    kind: "places",
    x: 155,
    y: 690,
    r: 27,
    detail: {
      type: "Place",
      story: "A major centre connected with the Cornovii and Roman power.",
      meaning: "Exchange, alliance and pressure.",
      symbolism: "Road, market, council and compromise.",
      appears: ["Blood and Honour"],
      related: ["Cornovii"],
    },
  },
  {
    id: "segontium",
    title: "Segontium",
    label: "Segontium",
    kind: "places",
    x: 255,
    y: 720,
    r: 27,
    detail: {
      type: "Place",
      story: "A Roman fort watching the western routes.",
      meaning: "Military presence on the edge of mountain and sea.",
      symbolism: "Fort, road, watch and control.",
      appears: ["Shadows of the Eagle"],
      related: ["Romans"],
    },
  },
  {
    id: "ridgeway",
    title: "Ridgeway",
    label: "Ridgeway",
    kind: "places",
    x: 355,
    y: 690,
    r: 27,
    detail: {
      type: "Place",
      story: "High ground, route and refuge.",
      meaning: "The land as protection and witness.",
      symbolism: "Hill, path, beacon and hidden movement.",
      appears: ["Blood and Honour", "Shadows of the Eagle"],
      related: ["Stone", "The Double Enemy"],
    },
  },
  {
    id: "din",
    title: "Din Lligwy",
    label: "Din\nLligwy",
    kind: "places",
    x: 375,
    y: 580,
    r: 27,
    detail: {
      type: "Place",
      story: "A settlement of craft, labour and Roman influence on Mona.",
      meaning: "Daily life under pressure.",
      symbolism: "Workshop, wall, iron, settlement and change.",
      appears: ["Shadows of the Eagle"],
      related: ["Mona"],
    },
  },

  {
    id: "varro",
    title: "Varro",
    label: "Varro",
    kind: "characters",
    x: 800,
    y: 555,
    r: 27,
    detail: {
      type: "Character Voice",
      tags: ["Roman", "Veteran"],
      story:
        "Varro carries the memory of Legio XX and the burden of what service has cost him.",
      meaning: "Duty fractured into chosen loyalty.",
      symbolism: "Boar, eagle, scar, oath and command.",
      appears: ["Blood and Honour", "Shadows of the Eagle"],
      related: ["Eagle", "Boar", "Lucius"],
    },
  },
  {
    id: "aerwyna",
    title: "Aerwyna",
    label: "Aerwyna",
    kind: "characters",
    x: 825,
    y: 650,
    r: 27,
    detail: {
      type: "Character Voice",
      tags: ["Ordovices", "Leader"],
      story:
        "Aerwyna’s authority grows through action, land, ritual and the choices made in crisis.",
      meaning: "Leadership rooted in belonging.",
      symbolism: "Torc, fire, mountain and oath.",
      appears: ["Shadows of the Eagle"],
      related: ["Ordovices", "Lucius", "Torc"],
    },
  },
  {
    id: "lucius",
    title: "Lucius",
    label: "Lucius",
    kind: "characters",
    x: 735,
    y: 720,
    r: 27,
    detail: {
      type: "Character Voice",
      tags: ["Roman", "Cornovii"],
      story: "Lucius stands between Roman and tribal worlds.",
      meaning: "A bridge between divided identities.",
      symbolism: "Hound, road, oath and divided blood.",
      appears: ["Blood and Honour", "Shadows of the Eagle"],
      related: ["Varro", "Aerwyna", "Cornovii"],
    },
  },
  {
    id: "julius",
    title: "Julius",
    label: "Julius",
    kind: "characters",
    x: 650,
    y: 705,
    r: 27,
    detail: {
      type: "Character Voice",
      tags: ["Veteran"],
      story: "Julius remains a man of iron, duty-bound even when wounded.",
      meaning: "Endurance and sacrifice.",
      symbolism: "Shield, line, wound and last stand.",
      appears: ["Blood and Honour", "Shadows of the Eagle"],
      related: ["The Double Enemy"],
    },
  },
  {
    id: "nestor",
    title: "Nestor",
    label: "Nestor",
    kind: "characters",
    x: 555,
    y: 615,
    r: 27,
    detail: {
      type: "Character Voice",
      tags: ["Physician", "Witness"],
      story:
        "Nestor carries healing, reason and helpless witness into the violence around him.",
      meaning: "Memory, compassion and survival under captivity.",
      symbolism: "Hands, medicine, captivity, oath and witness.",
      appears: ["Blood and Honour", "Shadows of the Eagle"],
      related: ["Mona", "Varro", "Raid on Mona"],
    },
  },

  {
    id: "arena",
    title: "Arena",
    label: "Arena",
    kind: "moments",
    x: 370,
    y: 750,
    r: 26,
    detail: {
      type: "Story Moment",
      story:
        "The arena is where Rome turns men into spectacle and Varro begins to transform survival into defiance.",
      meaning: "Public violence made political.",
      symbolism: "Sand, blood, crowd, gate and judgement.",
      appears: ["Blood and Honour"],
      related: ["Varro", "Deva"],
    },
  },
  {
    id: "forge",
    title: "The Forge",
    label: "The\nForge",
    kind: "moments",
    x: 410,
    y: 805,
    r: 26,
    detail: {
      type: "Story Moment",
      story:
        "A divided hall, a druidic intervention and a people forced to decide whether fear or oath will guide them.",
      meaning: "Leadership tested before witnesses.",
      symbolism: "Hammer, fire, hall, spear and earth.",
      appears: ["Shadows of the Eagle"],
      related: ["Aerwyna", "Cornovii", "Fire"],
    },
  },
  {
    id: "raid",
    title: "Raid on Mona",
    label: "Raid on\nMona",
    kind: "moments",
    x: 530,
    y: 805,
    r: 26,
    detail: {
      type: "Story Moment",
      story:
        "Aerwyna’s raid on Mona frees captive labourers and changes the balance of the island.",
      meaning: "Liberation with consequences.",
      symbolism: "Night, mine, salt-water, chain and flame.",
      appears: ["Shadows of the Eagle"],
      related: ["Mona", "Aerwyna", "Nestor"],
    },
  },
  {
    id: "double",
    title: "The Double Enemy",
    label: "The Double\nEnemy",
    kind: "moments",
    x: 580,
    y: 700,
    r: 26,
    detail: {
      type: "Story Moment",
      story:
        "Enemies press from two directions and Julius holds the line so others can live.",
      meaning: "Sacrifice at the narrowing point.",
      symbolism: "Pass, shield, rear-guard and last stand.",
      appears: ["Shadows of the Eagle"],
      related: ["Julius", "Brigantes", "Scoti"],
    },
  },
  {
    id: "ashes",
    title: "Ashes and Oaths",
    label: "Ashes and\nOaths",
    kind: "moments",
    x: 575,
    y: 760,
    r: 26,
    detail: {
      type: "Story Moment",
      story:
        "The eagle sinks beneath the lake and the silence becomes a requiem for an allegiance that has ended.",
      meaning: "Release from empire and the first breath of something new.",
      symbolism: "Lake, eagle, silence, mountain and oath.",
      appears: ["Shadows of the Eagle"],
      related: ["Eagle", "Water"],
    },
  },
  {
    id: "lights",
    title: "The Lights that Remain",
    label: "The Lights\nthat Remain",
    kind: "moments",
    x: 470,
    y: 805,
    r: 26,
    detail: {
      type: "Story Moment",
      story:
        "Blue-white lights move over the land as an older memory seems to breathe through the earth.",
      meaning: "Spiritual continuity beyond conquest.",
      symbolism: "Light, star, earth-breath, druid and ancient witness.",
      appears: ["Shadows of the Eagle"],
      related: ["Stone", "Fire"],
    },
  },
];

const LINKS = [
  ["sons", "books"],
  ["sons", "peoples"],
  ["sons", "symbols"],
  ["sons", "places"],
  ["sons", "characters"],
  ["sons", "moments"],
  ["sons", "pure"],
  ["books", "blood"],
  ["books", "shadows"],
  ["peoples", "romans"],
  ["peoples", "cornovii"],
  ["peoples", "ordovices"],
  ["peoples", "deceangli"],
  ["peoples", "brigantes"],
  ["peoples", "scoti"],
  ["symbols", "eagle"],
  ["symbols", "boar"],
  ["symbols", "torc"],
  ["symbols", "spear"],
  ["symbols", "fire"],
  ["symbols", "water"],
  ["symbols", "stone"],
  ["places", "deva"],
  ["places", "mona"],
  ["places", "viriconium"],
  ["places", "segontium"],
  ["places", "ridgeway"],
  ["places", "din"],
  ["characters", "varro"],
  ["characters", "aerwyna"],
  ["characters", "lucius"],
  ["characters", "julius"],
  ["characters", "nestor"],
  ["moments", "arena"],
  ["moments", "forge"],
  ["moments", "raid"],
  ["moments", "double"],
  ["moments", "ashes"],
  ["moments", "lights"],
];

const FALLBACK_SECTIONS = [
  "Architecture & Buildings",
  "Daily Life",
  "Government & Law",
  "Military Terms",
  "Roman Cuisine - Deep Dive",
  "Religion & Mythology",
  "Writing & Education",
];

function textBlob(node) {
  return [
    node.title,
    node.label,
    node.detail?.type,
    node.detail?.story,
    node.detail?.meaning,
    node.detail?.symbolism,
    ...(node.detail?.tags || []),
    ...(node.detail?.appears || []),
    ...(node.detail?.related || []),
  ]
    .join(" ")
    .replace(/\n/g, " ")
    .toLowerCase();
}


function findNodeByRelatedLabel(label) {
  const clean = String(label || "")
    .replace(/\s+/g, " ")
    .toLowerCase()
    .trim();

  return NODES.find((node) => {
    const title = String(node.title || "")
      .replace(/\s+/g, " ")
      .toLowerCase()
      .trim();

    const labelText = String(node.label || "")
      .replace(/\n/g, " ")
      .replace(/\s+/g, " ")
      .toLowerCase()
      .trim();

    return title === clean || labelText === clean;
  });
}

function GraphNode({ node, active, muted, onClick }) {
  const lines = String(node.label || node.title).split("\n");
  const fill = COLOUR[node.kind] || "#777";
  const fontSize = node.id === "sons" ? 15 : node.r > 40 ? 12 : 8.5;
  const labelY = lines.length > 1 ? -4 : 5;

  return (
    <g
      className={`sot-node ${active ? "is-active" : ""} ${
        muted ? "is-muted" : ""
      }`}
      transform={`translate(${node.x} ${node.y})`}
      onClick={onClick}
      role="button"
      tabIndex="0"
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onClick();
      }}
    >
      <circle r={node.r} fill={fill} />

      {node.icon && (
        <text
          className="sot-node-icon"
          y={node.r > 40 ? -18 : -10}
          textAnchor="middle"
        >
          {node.icon}
        </text>
      )}

      <text className="sot-node-text" textAnchor="middle" fontSize={fontSize}>
        {lines.map((line, i) => (
          <tspan key={`${node.id}-${i}`} x="0" y={labelY + i * fontSize * 1.15}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
}

function StoryMap({ selected, setSelected, matches, query }) {
  const byId = useMemo(() => new Map(NODES.map((n) => [n.id, n])), []);
  const searching = query.trim().length > 0;

  return (
    <svg
      className="sot-map-svg"
      viewBox="0 0 940 860"
      aria-label="Sons of the Standard thesaurus map"
    >
      <defs>
        <radialGradient id="sotGoldGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#f4d07a" stopOpacity="0.28" />
          <stop offset="100%" stopColor="#f4d07a" stopOpacity="0" />
        </radialGradient>

        <filter id="sotNodeGlow" x="-45%" y="-45%" width="190%" height="190%">
          <feDropShadow
            dx="0"
            dy="0"
            stdDeviation="6"
            floodColor="#f2c76d"
            floodOpacity="0.45"
          />
        </filter>
      </defs>

      <rect x="0" y="0" width="940" height="860" rx="18" fill="rgba(0,0,0,0.10)" />

      <circle cx="470" cy="380" r="135" className="sot-orbit" />
      <circle cx="470" cy="380" r="245" className="sot-orbit" />
      <circle cx="470" cy="380" r="340" className="sot-orbit" />
      <circle cx="470" cy="380" r="110" fill="url(#sotGoldGlow)" />

      <g className="sot-links">
        {LINKS.map(([aId, bId]) => {
          const a = byId.get(aId);
          const b = byId.get(bId);
          if (!a || !b) return null;
          const muted = searching && (!matches.has(aId) || !matches.has(bId));

          return (
            <line
              key={`${aId}-${bId}`}
              className={muted ? "is-muted" : ""}
              x1={a.x}
              y1={a.y}
              x2={b.x}
              y2={b.y}
            />
          );
        })}
      </g>

      <g className="sot-nodes">
        {NODES.map((node) => (
          <GraphNode
            key={node.id}
            node={node}
            active={selected?.id === node.id || (searching && matches.has(node.id))}
            muted={searching && !matches.has(node.id)}
            onClick={() => setSelected(node)}
          />
        ))}
      </g>
    </svg>
  );
}

function PanelBlock({ icon, title, text }) {
  if (!text) return null;

  return (
    <section className="sot-panel-block">
      <h3>
        {icon} {title}
      </h3>
      <p>{text}</p>
    </section>
  );
}

function InfoPanel({ node, onBack, onOpenPure, onRelatedHover, onRelatedLeave, onRelatedClick }) {
  const n = node || NODES[0];
  const detail = n.detail || {};

  return (
    <aside className="sot-detail-panel">
      <div className="sot-panel-top">
        <button type="button" onClick={onBack}>
          ← Back to graph
        </button>

        {n.action === "pure" && (
          <button type="button" onClick={onOpenPure}>
            Open pure thesaurus
          </button>
        )}
      </div>

      <div className="sot-panel-head">
        <div className={`sot-panel-medallion sot-${n.kind}`}>
          {n.kind === "pure" ? "SPQR" : n.icon || "✦"}
        </div>

        <div>
          <h2>{n.title}</h2>
          <p>
            Type: <span>{detail.type || n.kind}</span>
          </p>
        </div>
      </div>

      {!!detail.tags?.length && (
        <div className="sot-tag-row">
          {detail.tags.map((tag) => (
            <span key={tag}>{tag}</span>
          ))}
        </div>
      )}

      <PanelBlock icon="▣" title="In the story" text={detail.story} />
      <PanelBlock icon="♡" title="Meaning" text={detail.meaning} />
      <PanelBlock icon="◎" title="Symbolism" text={detail.symbolism} />

      {!!detail.appears?.length && (
        <section className="sot-panel-block">
          <h3>▤ Appears in</h3>
          <div className="sot-pill-row">
            {detail.appears.map((item) => (
              <span key={item}>{item}</span>
            ))}
          </div>
        </section>
      )}

      {!!detail.related?.length && (
        <section className="sot-panel-block">
          <h3>⌁ Related nodes</h3>
          <div className="sot-pill-row sot-related">
            {detail.related.map((item) => {
              const relatedNode = findNodeByRelatedLabel(item);

              return (
                <span
                  key={item}
                  role={relatedNode ? "button" : undefined}
                  tabIndex={relatedNode ? 0 : undefined}
                  onMouseEnter={() => relatedNode && onRelatedHover(relatedNode)}
                  onMouseLeave={onRelatedLeave}
                  onFocus={() => relatedNode && onRelatedHover(relatedNode)}
                  onBlur={onRelatedLeave}
                  onClick={() => relatedNode && onRelatedClick(relatedNode)}
                  onKeyDown={(event) => {
                    if (!relatedNode) return;
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onRelatedClick(relatedNode);
                    }
                  }}
                  title={relatedNode ? `Highlight ${relatedNode.title}` : "No graph node yet"}
                  style={{ cursor: relatedNode ? "pointer" : "default", opacity: relatedNode ? 1 : 0.55 }}
                >
                  {item}
                </span>
              );
            })}
          </div>
        </section>
      )}
    </aside>
  );
}

function PureThesaurusView({ data, onBack }) {
  const [activeSection, setActiveSection] = useState(null);
  const [activeSub, setActiveSub] = useState(null);

  const rawNodes = Array.isArray(data?.nodes) ? data.nodes : [];

  const sections = useMemo(() => {
    const found = rawNodes.filter(
      (n) => n.type === "category" && String(n.id || "").startsWith("sec_")
    );

    return found.length
      ? found
      : FALLBACK_SECTIONS.map((label, i) => ({
          id: `fallback_${i}`,
          label,
        }));
  }, [rawNodes]);

  const subSections = useMemo(() => {
    if (!activeSection) return [];

    return rawNodes
      .filter((n) => n.type === "category" && n.meta?.section === activeSection)
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [rawNodes, activeSection]);

  const terms = useMemo(() => {
    if (!activeSection || !activeSub) return [];

    return rawNodes
      .filter(
        (n) =>
          n.meta?.section === activeSection &&
          n.meta?.category === activeSub &&
          n.type !== "category"
      )
      .slice(0, 120);
  }, [rawNodes, activeSection, activeSub]);

  return (
    <div className="sot-pure-view">
      <div className="sot-pure-toolbar">
        <button type="button" onClick={onBack}>
          ← Back to Sons of the Standard map
        </button>

        {activeSection && (
          <button
            type="button"
            onClick={() => {
              setActiveSection(null);
              setActiveSub(null);
            }}
          >
            All categories
          </button>
        )}

        {activeSub && (
          <button type="button" onClick={() => setActiveSub(null)}>
            Back to {activeSection}
          </button>
        )}
      </div>

      <h2>Ancient Roman Terms Thesaurus</h2>
      <p>
        The original practical thesaurus is kept here as a focused Roman terms
        reference tool.
      </p>

      {!activeSection && (
        <div className="sot-pure-grid">
          {sections.map((s) => (
            <button key={s.id} type="button" onClick={() => setActiveSection(s.label)}>
              {s.label}
            </button>
          ))}
        </div>
      )}

      {activeSection && !activeSub && (
        <div className="sot-pure-grid">
          {(subSections.length
            ? subSections
            : [{ id: "none", label: "No subcategories found" }]
          ).map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => s.id !== "none" && setActiveSub(s.label)}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}

      {activeSection && activeSub && (
        <div className="sot-pure-terms">
          {(terms.length
            ? terms
            : [{ id: "none", label: "No terms found in this category yet." }]
          ).map((t) => (
            <article key={t.id}>
              <strong>{t.label || t.id}</strong>
              {t.meta?.gloss && <p>{t.meta.gloss}</p>}
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ThesaurusGraph({ data = { nodes: [], links: [] } }) {
  const [mode, setMode] = useState("story");
  const [selected, setSelected] = useState(null);
  const [hoveredRelated, setHoveredRelated] = useState(null);
  const [query, setQuery] = useState("");

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();

    if (!q) return new Set(NODES.map((n) => n.id));

    return new Set(
      NODES.filter((n) => textBlob(n).includes(q)).map((n) => n.id)
    );
  }, [query]);

  useEffect(() => {
    const q = query.trim().toLowerCase();

    if (!q) return;

    const exact = NODES.find((n) =>
      [n.title, n.label]
        .filter(Boolean)
        .some(
          (value) =>
            String(value).replace(/\n/g, " ").toLowerCase() === q
        )
    );

    if (exact) {
      setSelected(exact);
      return;
    }

    const first = NODES.find((n) => matches.has(n.id));

    if (first) setSelected(first);
  }, [query, matches]);

  function openPure() {
    setMode("pure");
    setSelected(null);
    setHoveredRelated(null);
  }

  function resetView() {
    setMode("story");
    setSelected(null);
    setHoveredRelated(null);
    setQuery("");
  }

  return (
    <div className="sot-thesaurus-page">
      <header className="sot-header">
        <div className="sot-brand-mark">SPQR</div>

        <div className="sot-title-block">
          <h1>Ancient Roman Terms Thesaurus</h1>
          <p>
            Explore words, symbols, peoples, places and story elements from the{" "}
            <em>Sons of the Standard</em> storyworld.
          </p>
        </div>

        <div className="sot-header-actions">
          <label className="sot-search">
            <span>⌕</span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search terms..."
            />
          </label>

          <button type="button" onClick={resetView}>
            Reset view
          </button>
        </div>
      </header>

      {mode === "pure" ? (
        <PureThesaurusView data={data} onBack={resetView} />
      ) : (
        <div className="sot-layout">
          <main className="sot-map-panel">
            <div className="sot-help-card">
              <strong>How to use</strong>
              <span>
                Click any bubble to explore. Search focuses the matching item.
                Select the pure thesaurus node for the original Roman terms tool.
              </span>
            </div>

            <StoryMap
              selected={hoveredRelated || selected}
              setSelected={(node) => {
                setSelected(node);
                setHoveredRelated(null);
              }}
              matches={matches}
              query={query}
            />

            
     
          </main>

          <InfoPanel
            node={selected || NODES[0]}
            onBack={() => {
              setSelected(null);
              setHoveredRelated(null);
            }}
            onOpenPure={openPure}
            onRelatedHover={setHoveredRelated}
            onRelatedLeave={() => setHoveredRelated(null)}
            onRelatedClick={(node) => {
              setSelected(node);
              setHoveredRelated(null);
            }}
          />
        </div>
      )}
    </div>
  );
}